/**
 * Per-part fingerprint of the game data (see `DataProfile` in @d2/protocol for what each part
 * covers and why). Computed the same way by the realm and by the browser from their own files.
 */
import type { ArchiveSet, ExcelTable } from '@d2/formats';
import type { GameData, TableName } from './index';

/** Mirrors `DataPart` in @d2/protocol (kept here so @d2/data needs no protocol dependency). */
export type FingerprintPart = 'world' | 'tiles' | 'units' | 'skills' | 'items' | 'sounds' | 'anim' | 'server';

/** Which tables make up each part. Every table of `TABLES` is in exactly one part. */
export const PART_TABLES: Record<Exclude<FingerprintPart, 'tiles'>, readonly TableName[]> = {
  world: ['Levels', 'LvlPrest', 'LvlTypes', 'LvlMaze', 'LvlWarp', 'LvlSub', 'objects', 'ObjMode'],
  units: ['monstats', 'monstats2', 'MonLvl', 'MonType', 'MonMode', 'SuperUniques', 'charstats', 'PlrMode', 'PlayerClass', 'UniquePrefix', 'UniqueSuffix', 'UniqueAppellation'],
  skills: ['skills', 'skilldesc', 'Missiles', 'states', 'Overlay', 'ElemTypes'],
  items: [
    'weapons', 'armor', 'misc', 'ItemTypes', 'ItemStatCost', 'Properties', 'MagicPrefix', 'MagicSuffix', 'RarePrefix', 'RareSuffix',
    'UniqueItems', 'SetItems', 'Sets', 'gems', 'Runes', 'automagic', 'qualityitems', 'lowqualityitems', 'inventory', 'belts', 'bodylocs', 'books', 'StorePage',
  ],
  sounds: ['Sounds', 'SoundEnviron'],
  anim: ['WeaponClass', 'Composit', 'ArmType', 'HitClass'],
  // Read only by the realm's simulation (drops, cube, economy, progression). The client loads
  // them with the rest but never consults them: the server sends prices, hire offers,
  // experience thresholds and shrine texts in its messages.
  server: ['TreasureClassEx', 'itemratio', 'MonItemPercent', 'experience', 'difficultylevels', 'shrines', 'objgroup', 'npc', 'hireling', 'cubemain', 'cubemod', 'cubetype', 'gamble', 'MonUMod', 'MonProp', 'MonSeq', 'MonEquip'],
};

/** Two FNV-1a 32-bit lanes → 16 hex digits. Not cryptographic: it detects different data, not forgeries. */
class Hasher {
  private a = 0x811c9dc5;
  private b = 0x01000193 ^ 0x9e3779b9;
  str(s: string): this {
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      this.a = Math.imul(this.a ^ c, 0x01000193);
      this.b = Math.imul(this.b ^ c, 0x01000193) ^ (this.b >>> 15);
    }
    this.a = Math.imul(this.a ^ 0x1f, 0x01000193); // separator
    this.b = Math.imul(this.b ^ 0x1f, 0x01000193);
    return this;
  }
  bytes(d: Uint8Array): this {
    for (let i = 0; i < d.length; i++) {
      this.a = Math.imul(this.a ^ d[i], 0x01000193);
      this.b = Math.imul(this.b ^ d[i], 0x01000193) ^ (this.b >>> 15);
    }
    return this;
  }
  hex(): string {
    return (this.a >>> 0).toString(16).padStart(8, '0') + (this.b >>> 0).toString(16).padStart(8, '0');
  }
}

/**
 * Adds a table in canonical form: header names and cells (trimmed) of every non-comment column,
 * row by row as the engine indexes them (`ExcelTable` already drops blank and "Expansion"
 * divider rows and ignores line endings).
 */
function hashTable(h: Hasher, name: string, t: ExcelTable | undefined): void {
  h.str(`#${name}`);
  if (!t) return;
  const cols = t.columns.map((c, i) => [c.trim(), i] as const).filter(([c]) => c !== '' && !c.startsWith('*'));
  h.str(cols.map(([c]) => c.toLowerCase()).join('\t'));
  for (const row of t.rows) h.str(cols.map(([, i]) => (row[i] ?? '').trim()).join('\t'));
}

/** Hash of one table in canonical form (exposed for tests and diagnostics). */
export function tableFingerprint(name: string, t: ExcelTable | undefined): string {
  const h = new Hasher();
  hashTable(h, name, t);
  return h.hex();
}

/** DS1/DT1 files the world tables reference, lower-cased and de-duplicated. */
export function tileFiles(data: GameData): string[] {
  const out = new Set<string>();
  const add = (f: string) => {
    if (f && f !== '0') out.add(`data\\global\\tiles\\${f.replace(/\//g, '\\')}`.toLowerCase());
  };
  for (const r of data.t.LvlPrest ?? []) for (let i = 1; i <= 6; i++) add(r.str(`File${i}`));
  for (const r of data.t.LvlTypes ?? []) for (let i = 1; i <= 32; i++) add(r.str(`File ${i}`));
  for (const r of data.t.LvlSub ?? []) add(r.str('File'));
  return [...out].sort();
}

export type Fingerprint = Record<FingerprintPart, string>;

/**
 * Fingerprints the loaded tables plus the map files and AnimData they depend on. Reads only
 * block-table sizes for unpatched map files and AnimData.D2 (which the client loads anyway).
 */
export async function dataFingerprint(data: GameData, vfs: Pick<ArchiveSet, 'size' | 'tryRead'>): Promise<Fingerprint> {
  const out = {} as Fingerprint;
  for (const [part, tables] of Object.entries(PART_TABLES) as [Exclude<FingerprintPart, 'tiles'>, readonly TableName[]][]) {
    const h = new Hasher();
    for (const t of tables) hashTable(h, t, data.t[t]);
    if (part === 'anim') h.bytes((await vfs.tryRead('data\\global\\AnimData.D2')) ?? new Uint8Array(0));
    out[part] = h.hex();
  }
  const h = new Hasher();
  for (const f of tileFiles(data)) h.str(`${f}=${(await vfs.size(f)) ?? -1}`);
  out.tiles = h.hex();
  return out;
}
